import { type TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, BrandS } from '@sorare/core/src/atoms/typography';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  MonetaryAmount,
  monetaryAmountFragment,
} from '@sorare/core/src/lib/monetaryAmount';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import type { PromoBanner_conversionCredit } from './__generated__/index.graphql';
import background from './assets/background.png';

const Root = styled(Horizontal).attrs({ gap: 3, center: true })`
  border-radius: var(--double-and-a-half-unit);
  padding: var(--double-unit);
  --border-gradient: var(--c-gradient-discount);
  ${borderGradientStyle};
  background-image: url(${background});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &.restricted {
    @media ${tabletAndAbove} {
      max-width: calc(44 * var(--unit));
    }
  }
`;
const StyledImg = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;
`;

type Props = {
  conversionCredit: Nullable<PromoBanner_conversionCredit>;
  restricted?: boolean;
};
export const PromoBanner = ({ conversionCredit, restricted }: Props) => {
  const { maxDiscount, percentageDiscount } = conversionCredit || {};
  const { main } = useAmountWithConversion({
    monetaryAmount: maxDiscount || MonetaryAmount.zero,
  });

  if (!conversionCredit) return null;

  return (
    <Root className={classNames({ restricted })}>
      <Vertical gap={0.5} center>
        <StyledImg src={`${FRONTEND_ASSET_HOST}/rewards/credit.png`} alt="" />
        <BrandS>
          <FormattedMessage
            {...glossary.percent}
            values={{ percent: percentageDiscount }}
          />
        </BrandS>
      </Vertical>
      <Vertical>
        <BrandS>
          <FormattedMessage
            id="PromoBanner.title"
            defaultMessage="Welcome Promo"
          />
        </BrandS>
        <BodyS color="var(--c-nd-700)">
          <FormattedMessage
            id="PromoBanner.description"
            defaultMessage="Get up to <b>{percentageDiscount, number, percent}</b> off your first purchase. Value up to {limit}."
            values={{
              percentageDiscount,
              limit: maxDiscount ? main : 0,
              b: (chunks: ReactNode[]) => {
                return (
                  <BodyS as="span" color="var(--c-white)" bold>
                    {chunks}
                  </BodyS>
                );
              },
            }}
          />
        </BodyS>
      </Vertical>
    </Root>
  );
};

PromoBanner.fragments = {
  conversionCredit: gql`
    fragment PromoBanner_conversionCredit on ConversionCredit {
      id
      percentageDiscount
      maxDiscount {
        ...MonetaryAmountFragment_monetaryAmount
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<PromoBanner_conversionCredit>,
};
